import { ThunkResult } from 'redux/types';
import * as errorDispatch from 'redux/ducks/error';
import { ErrorActions } from 'redux/ducks/error';
import { AxiosResponse } from 'axios';
import { DataTransfer } from '@esmiley/frontend-core';
import { compare } from 'fast-json-patch';
import {
  MonthlyReportRecipient,
  MonthlyReportRecipientActions,
  MonthlyReportRecipientActionTypes,
  MonthlyReportRecipientState
} from 'redux/ducks/monthlyReportRecipients/types';

const transfer = new DataTransfer();

export const initialState: MonthlyReportRecipientState = {
  recipients: [],
  state: 'init'
};

export default function reducer(
  state: MonthlyReportRecipientState = initialState,
  action: MonthlyReportRecipientActions
): MonthlyReportRecipientState {
  switch (action.type) {
    case MonthlyReportRecipientActionTypes.GET_MONTHLY_REPORT_RECIPIENTS_REQUEST:
      return { ...state, state: 'loading' };
    case MonthlyReportRecipientActionTypes.GET_MONTHLY_REPORT_RECIPIENTS:
      return { ...state, recipients: action.payload, state: 'loaded' };
    case MonthlyReportRecipientActionTypes.UPDATE_MONTHLY_REPORT_RECIPIENT_REQUEST:
    case MonthlyReportRecipientActionTypes.DELETE_MONTHLY_REPORT_RECIPIENT_REQUEST:
    case MonthlyReportRecipientActionTypes.CREATE_MONTHLY_REPORT_RECIPIENT_REQUEST:
      return { ...state, state: 'pending' };
    case MonthlyReportRecipientActionTypes.UPDATE_MONTHLY_REPORT_RECIPIENT: {
      return {
        ...state,
        recipients: state.recipients.map((r) => (r.id === action.payload.id ? action.payload : r)),
        state: 'loaded'
      };
    }
    case MonthlyReportRecipientActionTypes.DELETE_MONTHLY_REPORT_RECIPIENT: {
      return {
        ...state,
        recipients: state.recipients.filter((recipient) => recipient.id !== action.payload),
        state: 'loaded'
      };
    }
    case MonthlyReportRecipientActionTypes.CREATE_MONTHLY_REPORT_RECIPIENT: {
      return {
        ...state,
        recipients: [...state.recipients, action.payload],
        state: 'loaded'
      };
    }
    default:
      return state;
  }
}

export function getRecipients(): ThunkResult<
  Promise<MonthlyReportRecipientActions | ErrorActions>,
  MonthlyReportRecipientActions | ErrorActions
> {
  return async (dispatch) => {
    try {
      dispatch({ type: MonthlyReportRecipientActionTypes.GET_MONTHLY_REPORT_RECIPIENTS_REQUEST });
      const response = (await transfer.get(
        '/foodwaste/monthly-reports/recipients'
      )) as AxiosResponse<MonthlyReportRecipient[]>;
      return dispatch({
        type: MonthlyReportRecipientActionTypes.GET_MONTHLY_REPORT_RECIPIENTS,
        payload: response.data
      });
    } catch (error: unknown) {
      return dispatch(errorDispatch.displayError(error as Error));
    }
  };
}

export function updateRecipient(
  id: MonthlyReportRecipient['id'],
  update: Partial<MonthlyReportRecipient>
): ThunkResult<
  Promise<MonthlyReportRecipientActions | ErrorActions>,
  MonthlyReportRecipientActions | ErrorActions
> {
  return async (dispatch, getState) => {
    try {
      const { recipients } = getState().monthlyReportRecipients;
      const old = recipients.find((recipient) => recipient.id === id);

      if (!old) {
        return dispatch(errorDispatch.displayError(new Error(`Recipient with id ${id} not found`)));
      }

      const patchOps = compare(old, { ...old, ...update });

      dispatch({ type: MonthlyReportRecipientActionTypes.UPDATE_MONTHLY_REPORT_RECIPIENT_REQUEST });
      const response = (await transfer.patch(
        `/foodwaste/monthly-reports/recipients/${id}`,
        patchOps
      )) as AxiosResponse<MonthlyReportRecipient>;
      return dispatch({
        type: MonthlyReportRecipientActionTypes.UPDATE_MONTHLY_REPORT_RECIPIENT,
        payload: response.data
      });
    } catch (error: unknown) {
      return dispatch(errorDispatch.displayError(error as Error));
    }
  };
}

export function deleteRecipient(
  id: MonthlyReportRecipient['id']
): ThunkResult<
  Promise<MonthlyReportRecipientActions | ErrorActions>,
  MonthlyReportRecipientActions | ErrorActions
> {
  return async (dispatch) => {
    try {
      dispatch({ type: MonthlyReportRecipientActionTypes.DELETE_MONTHLY_REPORT_RECIPIENT_REQUEST });
      await transfer.delete(`/foodwaste/monthly-reports/recipients/${id}`);
      return dispatch({
        type: MonthlyReportRecipientActionTypes.DELETE_MONTHLY_REPORT_RECIPIENT,
        payload: id
      });
    } catch (error: unknown) {
      return dispatch(errorDispatch.displayError(error as Error));
    }
  };
}

export function createRecipient(
  recipient: Omit<MonthlyReportRecipient, 'id' | 'accountId' | 'userId'>
): ThunkResult<
  Promise<MonthlyReportRecipientActions | ErrorActions>,
  MonthlyReportRecipientActions | ErrorActions
> {
  return async (dispatch) => {
    try {
      dispatch({ type: MonthlyReportRecipientActionTypes.CREATE_MONTHLY_REPORT_RECIPIENT_REQUEST });
      const response = (await transfer.post(
        '/foodwaste/monthly-reports/recipients',
        recipient
      )) as AxiosResponse<MonthlyReportRecipient>;
      return dispatch({
        type: MonthlyReportRecipientActionTypes.CREATE_MONTHLY_REPORT_RECIPIENT,
        payload: response.data
      });
    } catch (error: unknown) {
      return dispatch(errorDispatch.displayError(error as Error));
    }
  };
}

export function createAdminRecipient(
  recipient: Omit<MonthlyReportRecipient, 'id' | 'accountId' | 'userId'>,
  accountId: string
): ThunkResult<
  Promise<MonthlyReportRecipientActions | ErrorActions>,
  MonthlyReportRecipientActions | ErrorActions
> {
  return async (dispatch) => {
    try {
      dispatch({ type: MonthlyReportRecipientActionTypes.CREATE_MONTHLY_REPORT_RECIPIENT_REQUEST });
      const response = (await transfer.post(
        `/foodwaste/admin/accounts/${accountId}/monthly-reports/recipients`,
        recipient
      )) as AxiosResponse<MonthlyReportRecipient>;
      return dispatch({
        type: MonthlyReportRecipientActionTypes.CREATE_MONTHLY_REPORT_RECIPIENT,
        payload: response.data
      });
    } catch (error: unknown) {
      return dispatch(errorDispatch.displayError(error as Error));
    }
  };
}

export function deleteAdminRecipient(
  id: MonthlyReportRecipient['id'],
  accountId: string
): ThunkResult<
  Promise<MonthlyReportRecipientActions | ErrorActions>,
  MonthlyReportRecipientActions | ErrorActions
> {
  return async (dispatch) => {
    try {
      dispatch({ type: MonthlyReportRecipientActionTypes.DELETE_MONTHLY_REPORT_RECIPIENT_REQUEST });
      await transfer.delete(
        `/foodwaste/admin/accounts/${accountId}/monthly-reports/recipients/${id}`
      );
      return dispatch({
        type: MonthlyReportRecipientActionTypes.DELETE_MONTHLY_REPORT_RECIPIENT,
        payload: id
      });
    } catch (error: unknown) {
      return dispatch(errorDispatch.displayError(error as Error));
    }
  };
}
